import { Capacitor } from "@capacitor/core";
import { ScreenOrientation, type ScreenOrientationResult } from "@capacitor/screen-orientation";

/**
 * Screen orientation lock types.
 * Subset of OrientationLockType from @capacitor/screen-orientation.
 */
export type OrientationLockType =
  | "natural"
  | "portrait"
  | "portrait-primary"
  | "portrait-secondary"
  | "landscape"
  | "landscape-primary"
  | "landscape-secondary";

/**
 * Handle for managing screen orientation event listeners.
 */
export interface ScreenOrientationListenerHandle {
  remove: () => Promise<void>;
}

/**
 * Gets the current screen orientation.
 * On native platforms, returns the current orientation type.
 * On web, returns undefined.
 *
 * @returns The current orientation type or undefined on web
 */
export async function getScreenOrientation() {
  if (!Capacitor.isNativePlatform()) {
    return undefined;
  }

  const result = await ScreenOrientation.orientation();
  return result.type;
}

/**
 * Locks the screen to a specific orientation.
 * On native platforms, locks the screen orientation.
 * On web, this is a no-op.
 *
 * @param orientation - The orientation to lock to
 */
export async function lockScreenOrientation(orientation: OrientationLockType): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await ScreenOrientation.lock({ orientation });
}

/**
 * Unlocks the screen orientation.
 * On native platforms, allows the screen to rotate freely.
 * On web, this is a no-op.
 */
export async function unlockScreenOrientation(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await ScreenOrientation.unlock();
}

/**
 * Adds a listener for screen orientation changes.
 * On native platforms, listens for orientation change events.
 * On web, returns a no-op listener handle.
 *
 * @param callback - The callback to invoke when orientation changes
 * @returns A handle that can be used to remove the listener
 */
export async function addScreenOrientationListener(
  callback: (orientation: ScreenOrientationResult["type"]) => void
): Promise<ScreenOrientationListenerHandle> {
  if (!Capacitor.isNativePlatform()) {
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  return await ScreenOrientation.addListener("screenOrientationChange", (result) => {
    callback(result.type);
  });
}
